import type { SignatureCard } from './signature'
import { placeLines } from './signature'
import { vcfFileName } from './businessCard'
import { initialsOf } from './initials'

/**
 * The business card as a PICTURE, to keep in the camera roll. David, 6 Oct:
 * "a download image (jpg or png) option so they can keep it in photo reel."
 *
 * ── DRAWN ON A CANVAS, NOT SNAPSHOTTED FROM THE PAGE ────────────────────────
 *
 * The app already snapshots markup with html-to-image (supportMedia.ts), and
 * that is the wrong tool here: it renders through an SVG <foreignObject>, and
 * iOS Safari — the phone this button exists for — routinely draws the images
 * inside one blank on the first pass (the QR, the photo, the logo: everything
 * that matters). A support screenshot can afford that; a card you show a
 * client cannot. So the card is drawn again, deliberately, with the 2D canvas:
 * same tokens, same order, every pixel known.
 *
 * ── 1080 WIDE, ABOUT A PHONE SCREEN TALL ────────────────────────────────────
 *
 * Opened full screen in Photos, the picture IS the card and the QR is ~half
 * the screen wide — scannable from across a table, which is what showing a
 * card from your camera roll is for. PNG, not JPEG: the code's edges and the
 * small print stay sharp, and a JPEG's ringing around QR modules is exactly
 * what a cheap camera trips on.
 *
 * ── THE SVG ARTWORK COMES FROM THE LIVE CARD FACE ───────────────────────────
 *
 * The mark and the contact icons are React components, not files. Rather than
 * keep a second copy of the artwork, the face that is on screen is passed in
 * and its own <svg>s are serialised — so the picture can never drift from the
 * card. The lockup is the same public file the face points at.
 */

export const CARD_IMAGE_W = 1080
const PAD = 84
const FONT = '"Figtree","Gotham","Helvetica Neue",Arial,sans-serif'

/** Tokens, written out — a canvas has no CSS. Same values as colors.css. */
const C = {
  blue700: '#004489',
  blue800: '#003567',
  blue900: '#002b57',
  blue050: '#f3f7fb',
  blue100: '#e6edf5',
  logoBlue: '#034792',
  logoRed: '#ff2e22',
  strong: '#1a1a1a',
  body: '#4d4d4d',
  muted: '#828282',
  subtle: '#f6f6f6',
  rule: '#dcdcdc',
  white: '#ffffff',
}

export type ImageContact = { kind: 'email' | 'mobile' | 'office' | 'place'; lines: string[]; tag: string | null }

/** The words on the picture, in order — pure, so the suite can check them.
 *  The face's rows exactly: email, mobile, office, place (placeLines), and
 *  "Conetec" at the foot for everybody (David, 6 Oct). */
export function cardImageLines(
  card: SignatureCard,
  labels: { mobile: string; office: string } = { mobile: 'Mobile', office: 'Office' },
): { name: string; role: string | null; contacts: ImageContact[]; footer: string } {
  // 178: the company address when there is one — never a personal one.
  const contacts: ImageContact[] = card.workEmail ? [{ kind: 'email', lines: [card.workEmail], tag: null }] : []
  if (card.workPhone) contacts.push({ kind: 'mobile', lines: [card.workPhone], tag: labels.mobile })
  if (card.officePhone) contacts.push({ kind: 'office', lines: [card.officePhone], tag: labels.office })
  const place = placeLines(card)
  if (place.length > 0) contacts.push({ kind: 'place', lines: place, tag: null })
  return { name: card.displayName, role: card.jobTitle, contacts, footer: 'Conetec' }
}

/** "ana-maria-rojas-conetec-card.png" — the .vcf's name, so the two sit together. */
export function cardImageFileName(card: Pick<SignatureCard, 'displayName'>): string {
  return vcfFileName(card).replace(/\.vcf$/, '-card.png')
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`could not load ${src.slice(0, 40)}`))
    img.src = src
  })
}

/** An inline <svg> from the page, as an image, with currentColor resolved. */
async function svgImage(svg: SVGElement | null | undefined, color: string): Promise<HTMLImageElement | null> {
  if (!svg) return null
  const clone = svg.cloneNode(true) as SVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('color', color)
  // Through the CSSOM, not setAttribute('style'): a CSP without
  // 'unsafe-inline' may refuse a style ATTRIBUTE, never a CSSOM write (SEC-5).
  clone.style.color = color
  // Width/height in user units so Firefox will draw it at all.
  if (!clone.getAttribute('width')) clone.setAttribute('width', '24')
  if (!clone.getAttribute('height')) clone.setAttribute('height', '24')
  const xml = new XMLSerializer().serializeToString(clone)
  try {
    return await loadImage('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml))
  } catch {
    return null
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** Shrink a font until the text fits — a long name or address must not run off the picture. */
function fit(ctx: CanvasRenderingContext2D, text: string, weight: number, size: number, max: number, min = 22): number {
  let s = size
  ctx.font = `${weight} ${s}px ${FONT}`
  while (s > min && ctx.measureText(text).width > max) {
    s -= 2
    ctx.font = `${weight} ${s}px ${FONT}`
  }
  return s
}

export async function drawCardImage(opts: {
  card: SignatureCard
  /** The QR data URL the face is showing. */
  qr: string
  /** The photo data URL, or null for initials. */
  photo: string | null
  /** The live `.bizcard` element, for its SVG artwork. */
  face: HTMLElement | null
  /** null = no lockup on the band (CARD_SHOWS_LOGO, 11 Oct). */
  logoSrc: string | null
  scanLabel: string
  labels: { mobile: string; office: string }
}): Promise<Blob> {
  const { card, qr, photo, face, logoSrc, scanLabel, labels } = opts
  const lines = cardImageLines(card, labels)
  const W = CARD_IMAGE_W

  // The fonts must be LOADED, not merely declared, or the first picture of the
  // day is drawn in Arial. Failure here is cosmetic, so it is not fatal.
  try {
    await Promise.all([
      document.fonts.load(`600 64px ${FONT}`),
      document.fonts.load(`500 40px ${FONT}`),
      document.fonts.load(`400 36px ${FONT}`),
    ])
  } catch {
    /* fall back to the stack */
  }

  const [qrImg, logoImg, photoImg, markImg, markBlue, ...icons] = await Promise.all([
    loadImage(qr),
    logoSrc ? loadImage(logoSrc).catch(() => null) : Promise.resolve(null),
    photo ? loadImage(photo).catch(() => null) : Promise.resolve(null),
    svgImage(face?.querySelector<SVGElement>('.bizcard__watermark svg'), C.white),
    svgImage(face?.querySelector<SVGElement>('.bizcard__qrmark svg'), C.logoBlue),
    // Each row's glyph by the row's data-kind, so the order cannot drift.
    ...lines.contacts.map((c) =>
      svgImage(face?.querySelector<SVGElement>(`.bizcard__contact li[data-kind="${c.kind}"] .bizcard__icon svg`), C.blue700),
    ),
  ])

  // ── Layout, top to bottom. Heights are computed first so the canvas is
  //    exactly as tall as the card. ──
  const BAND = 500
  const R = 150 // photo radius
  const nameY = BAND + R + 12 + 92
  const roleY = nameY + (lines.role ? 62 : 0)
  const ruleY = roleY + 56
  const LINE = 72 // one row
  const SUB = 46 // each extra line of a multi-line row (the address)
  const rowH = (c: ImageContact) => LINE + (c.lines.length - 1) * SUB
  const contactsY = ruleY + 64
  const panelY = contactsY + lines.contacts.reduce((h, c) => h + rowH(c), 0) - LINE / 2 + 44
  const TILE = 560
  const panelH = 48 + TILE + 40 + 44 + 40
  const entityY = panelY + panelH + 72
  const H = entityY + 72

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot draw the card.')
  ctx.textBaseline = 'alphabetic'

  ctx.fillStyle = C.white
  ctx.fillRect(0, 0, W, H)

  // The band: the face's 135deg gradient, its faint mark, the lockup, the red rule.
  const g = ctx.createLinearGradient(0, 0, W, BAND)
  g.addColorStop(0, C.blue700)
  g.addColorStop(0.55, C.blue800)
  g.addColorStop(1, C.blue900)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, BAND)
  if (markImg) {
    ctx.save()
    ctx.globalAlpha = 0.09
    ctx.translate(W - 200, 40)
    ctx.rotate((-8 * Math.PI) / 180)
    ctx.drawImage(markImg, -420, -420, 840, 824)
    ctx.restore()
  }
  if (logoImg) {
    const lw = 340
    ctx.drawImage(logoImg, PAD, 92, lw, (lw * 58.33) / 196.79)
  }
  ctx.fillStyle = C.logoRed
  ctx.fillRect(0, BAND - 8, W, 8)

  // The face: a white ring and a soft shadow, then the photo or the initials.
  const cx = W / 2
  const cy = BAND
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.18)'
  ctx.shadowBlur = 36
  ctx.shadowOffsetY = 10
  ctx.beginPath()
  ctx.arc(cx, cy, R + 12, 0, Math.PI * 2)
  ctx.fillStyle = C.white
  ctx.fill()
  ctx.restore()
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, R, 0, Math.PI * 2)
  ctx.clip()
  if (photoImg) {
    const side = Math.min(photoImg.width, photoImg.height)
    ctx.drawImage(photoImg, (photoImg.width - side) / 2, (photoImg.height - side) / 2, side, side, cx - R, cy - R, R * 2, R * 2)
  } else {
    const pg = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R)
    pg.addColorStop(0, C.blue050)
    pg.addColorStop(1, C.blue100)
    ctx.fillStyle = pg
    ctx.fillRect(cx - R, cy - R, R * 2, R * 2)
    ctx.fillStyle = C.blue700
    ctx.font = `600 110px ${FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(initialsOf(card.displayName), cx, cy + 4)
    ctx.textBaseline = 'alphabetic'
  }
  ctx.restore()

  // Name and role.
  ctx.textAlign = 'center'
  ctx.fillStyle = C.strong
  fit(ctx, lines.name, 600, 64, W - PAD * 2, 36)
  ctx.fillText(lines.name, cx, nameY)
  if (lines.role) {
    ctx.fillStyle = C.blue700
    fit(ctx, lines.role, 500, 40, W - PAD * 2, 26)
    ctx.fillText(lines.role, cx, roleY)
  }
  ctx.fillStyle = C.rule
  ctx.fillRect(PAD, ruleY, W - PAD * 2, 2)

  // The contact lines, as one left-aligned block centred on the picture —
  // the face's own layout, icon discs and all; the address runs over lines.
  const ICON = 56
  const GAP = 24
  const TAG = 26
  const tagW = (c: ImageContact) => {
    if (!c.tag) return 0
    ctx.font = `600 ${TAG}px ${FONT}`
    return ctx.measureText(c.tag.toUpperCase()).width + 20
  }
  const room = (c: ImageContact) => W - PAD * 2 - ICON - GAP - tagW(c)
  let textSize = 36
  for (const c of lines.contacts) for (const l of c.lines) textSize = Math.min(textSize, fit(ctx, l, 400, 36, room(c), 24))
  ctx.font = `400 ${textSize}px ${FONT}`
  const widest = Math.max(
    ...lines.contacts.map((c) => {
      ctx.font = `400 ${textSize}px ${FONT}`
      return Math.max(...c.lines.map((l) => ctx.measureText(l).width)) + tagW(c)
    }),
  )
  const blockX = Math.max(PAD, (W - (ICON + GAP + widest)) / 2)
  let rowTop = contactsY
  lines.contacts.forEach((c, i) => {
    const mid = rowTop
    ctx.beginPath()
    ctx.arc(blockX + ICON / 2, mid, ICON / 2, 0, Math.PI * 2)
    ctx.fillStyle = C.blue050
    ctx.fill()
    const ic = icons[i]
    if (ic) ctx.drawImage(ic, blockX + ICON / 2 - 15, mid - 15, 30, 30)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.font = `400 ${textSize}px ${FONT}`
    ctx.fillStyle = C.body
    c.lines.forEach((l, k) => ctx.fillText(l, blockX + ICON + GAP, mid + 1 + k * SUB))
    if (c.tag) {
      const x = blockX + ICON + GAP + ctx.measureText(c.lines[0]).width + 20
      ctx.font = `600 ${TAG}px ${FONT}`
      ctx.fillStyle = C.muted
      ctx.fillText(c.tag.toUpperCase(), x, mid + 2)
    }
    ctx.textBaseline = 'alphabetic'
    rowTop += rowH(c)
  })

  // The code: a soft panel, a white tile (its quiet zone), the mark in the middle.
  ctx.fillStyle = C.subtle
  roundRect(ctx, PAD, panelY, W - PAD * 2, panelH, 28)
  ctx.fill()
  const tileX = cx - TILE / 2
  const tileY = panelY + 48
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.10)'
  ctx.shadowBlur = 16
  ctx.shadowOffsetY = 4
  ctx.fillStyle = C.white
  roundRect(ctx, tileX, tileY, TILE, TILE, 24)
  ctx.fill()
  ctx.restore()
  // Nearest-neighbour: a QR scaled with smoothing grows grey edges.
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(qrImg, tileX + 40, tileY + 40, TILE - 80, TILE - 80)
  ctx.imageSmoothingEnabled = true
  if (markBlue) {
    const M = 88
    ctx.fillStyle = C.white
    roundRect(ctx, cx - M / 2, tileY + TILE / 2 - M / 2, M, M, 10)
    ctx.fill()
    ctx.drawImage(markBlue, cx - 28, tileY + TILE / 2 - 28, 56, 55)
  }
  ctx.fillStyle = C.strong
  ctx.textAlign = 'center'
  ctx.font = `600 36px ${FONT}`
  ctx.fillText(scanLabel, cx, tileY + TILE + 40 + 30)

  // "Conetec" for everybody, never the registered entity (David, 6 Oct).
  ctx.fillStyle = C.muted
  ctx.font = `500 26px ${FONT}`
  ctx.fillText(lines.footer.toUpperCase(), cx, entityY)

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('This browser cannot draw the card.')
  return blob
}
